import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom, Observable } from 'rxjs';
import { URLPaths } from '../../common/utilities/urlPaths.config';
import { ApiheaderService } from '../../common/services/apiheader.service';
import { CommonService } from '../../common/services/common.service';
import { ConsultantDetailsService } from '../../common/services/consultant-details.service';
import { SessionStorageService } from '../../common/services/session-storage.service';
import { LocalStorageService } from '../../common/services/local-storage.service';
import { commonFunctions } from '../../common/classes/common.class';
import { ProjectDetailsTableData } from '../models/project-details.types';

@Injectable({
  providedIn: 'root'
})
export class ProjectDetailsApiService {

  constructor(
    private http: HttpClient,
    private commonService: CommonService,
    private consultantDetailsService: ConsultantDetailsService
  ) {}

  // ==========================================
  // Reference Data APIs
  // ==========================================

  async getClients(): Promise<any[]> {
    return await this.commonService.getClientswithInetrnalCode('projectDetails');
  }

  async getAllClients(): Promise<any[]> {
    return await this.commonService.getAllClients('projectDetails');
  }

  async getCompanies(): Promise<any[]> {
    return await this.commonService.getAllCompanies('projectDetails');
  }

  async getProjectTypes(): Promise<any[]> {
    return await this.commonService.getProjectTypes();
  }

  async getInvoiceFormats(): Promise<any[]> {
    return await this.commonService.getInvoiceFormats();
  }

  async getPromptPaymentPeriodTypes(): Promise<any[]> {
    return await this.commonService.getPromptPaymentPeriodTypes();
  }

  async getTypesOfService(): Promise<any[]> {
    return await this.commonService.getTypesOfService();
  }

  async getPeriodMonths(): Promise<any[]> {
    return await this.commonService.getPeriodMonths();
  }

  async getProjectThemes(): Promise<any[]> {
    return await this.commonService.getProjectThemes();
  }

  async getCountriesAndStates(): Promise<any[]> {
    return await this.commonService.getCountryDetails();
  }

  async getSalesTaxDetails(): Promise<any[]> {
    return await this.commonService.getSalesTaxDetails();
  }

  getEmployees(): Promise<any[]> {
    return new Promise((resolve) => {
      this.consultantDetailsService.getConsultantDetails().subscribe((details) => {
        resolve(details.map(d => d.Employee));
      });
    });
  }

  async getProjectsAndContacts(client: string): Promise<any> {

    const params = new HttpParams().append('client', client);

    const result = await firstValueFrom(
      this.http.get<any>(
        URLPaths.URL_PROJECT_AND_CONTACT_DETAILS,
        {
          headers: ApiheaderService.getHeaders(),
          params
        }
      )
    );

    SessionStorageService.setSessionStorageValueBasedOnKey(
      `projects-contacts_${client}`,
      JSON.stringify(result)
    );

    return result;
  }
  async getProjectDetailsTables(clientCode: string, projectCode: string): Promise<ProjectDetailsTableData> {
    const params = new HttpParams()
      .append('client', clientCode)
      .append('project', projectCode);
    return await firstValueFrom(
      this.http.get<ProjectDetailsTableData>(URLPaths.URL_PROJECT_DETAIL_TABLES, {
        headers: ApiheaderService.getHeaders(),
        params
      })
    );
  }

  async getPurchaseOrdersForProject(clientCode: string, projectCode: string): Promise<any[]> {
    return await this.commonService.getPurchaseOrderDetails(clientCode, projectCode);
  }

  // ==========================================
  // ID Resolvers & Lookup Helpers
  // ==========================================

  async getCompanyId(company: string): Promise<number> {
    return await this.commonService.getCompanyId(company);
  }

  async getProjectTypeId(projectType: string): Promise<number> {
    return await this.commonService.getProjectTypeId(projectType);
  }

  async getInvoiceFormatId(invoiceFormat: string): Promise<number> {
    return await this.commonService.getInvoiceFormatId(invoiceFormat);
  }

  async getPromptPaymentPeriodTypeId(periodType: string): Promise<number> {
    return await this.commonService.getPromptPaymentPeriodTypeId(periodType);
  }

  async getStateId(country: string, state: string): Promise<number> {
    return await this.commonService.getStateId(country, state);
  }

  async getCountryId(country: string): Promise<number> {
    return await this.commonService.getCountryId(country);
  }

  async getPeriodMonthId(month: string): Promise<number> {
    return await this.commonService.getPeriodMonthId(month);
  }

  async getCurrencyCode(countryCode: string): Promise<string> {
    return await this.commonService.getCurrencyCode(countryCode);
  }

  async getProjectThemeCode(theme: string): Promise<string> {
    return await this.commonService.getProjectThemeCode(theme);
  }

  // ==========================================
  // Project & Contact CRUD
  // ==========================================

  saveProject(payload: any): Observable<any> {
    return this.http.post(
      URLPaths.URL_PROJECT,
      { save_project_details: payload },
      { headers: ApiheaderService.getHeaders() }
    );
  }

  updateProject(payload: any): Observable<any> {
    return this.http.put(
      URLPaths.URL_PROJECT,
      { update_project_details: payload },
      { headers: ApiheaderService.getHeaders() }
    );
  }

  saveContact(payload: any): Observable<any> {
    return this.http.post(
      URLPaths.URL_CONTACT_DETAILS,
      { save_contact_details: payload },
      { headers: ApiheaderService.getHeaders() }
    );
  }

  updateContact(payload: any): Observable<any> {
    return this.http.put(
      URLPaths.URL_CONTACT_DETAILS,
      { update_contact_details: payload },
      { headers: ApiheaderService.getHeaders() }
    );
  }

  // ==========================================
  // Consultant Project Rates CRUD
  // ==========================================

  saveConsultantProjectRate(payload: any): Observable<any> {
    return this.http.post(
      URLPaths.URL_CONSULTANT_PROJECT_RATES,
      { save_consultant_project_rate: payload },
      { headers: ApiheaderService.getHeaders() }
    );
  }

  updateConsultantProjectRates(payloadArray: any[]): Observable<any> {
    return this.http.put(
      URLPaths.URL_CONSULTANT_PROJECT_RATES,
      { update_consultant_project_rate: payloadArray },
      { headers: ApiheaderService.getHeaders() }
    );
  }

  deleteConsultantProjectRates(payloadArray: any[]): Observable<any> {
    return this.http.delete(
      URLPaths.URL_CONSULTANT_PROJECT_RATES,
      {
        body: { delete_consultant_project_rate: payloadArray },
        headers: ApiheaderService.getHeaders()
      }
    );
  }

  // ==========================================
  // Project Tasks CRUD
  // ==========================================

  saveProjectTask(payload: any): Observable<any> {
    return this.http.post(
      URLPaths.URL_PROJECT_TASKS,
      { save_project_task: payload },
      { headers: ApiheaderService.getHeaders() }
    );
  }

  updateProjectTasks(payloadArray: any[]): Observable<any> {
    return this.http.post(
      URLPaths.URL_PROJECT_TASKS,
      { save_project_task: payloadArray },
      { headers: ApiheaderService.getHeaders() }
    );
  }

  deleteProjectTasks(payloadArray: any[]): Observable<any> {
    return this.http.delete(
      URLPaths.URL_PROJECT_TASKS,
      {
        body: { delete_project_task: payloadArray },
        headers: ApiheaderService.getHeaders()
      }
    );
  }

  // ==========================================
  // Theme Allocation CRUD
  // ==========================================

  saveThemeAllocation(payload: any): Observable<any> {
    return this.http.post(
      URLPaths.URL_PROJECT_THEME_ALLOCATION,
      { save_project_theme_allocation: payload },
      { headers: ApiheaderService.getHeaders() }
    );
  }

  updateThemeAllocation(payloadArray: any[]): Observable<any> {
    return this.http.put(
      URLPaths.URL_PROJECT_THEME_ALLOCATION,
      { update_project_theme_allocation: payloadArray },
      { headers: ApiheaderService.getHeaders() }
    );
  }

  deleteThemeAllocation(payloadArray: any[]): Observable<any> {
    return this.http.delete(
      URLPaths.URL_PROJECT_THEME_ALLOCATION,
      {
        body: { delete_project_theme_allocation: payloadArray },
        headers: ApiheaderService.getHeaders()
      }
    );
  }

  // ==========================================
  // Prepayment Details CRUD
  // ==========================================

  savePrepaymentDetails(payload: any): Observable<any> {
    return this.http.post(
      URLPaths.URL_PREPAYMENT_DETAILS,
      { save_prepayment_details: payload },
      { headers: ApiheaderService.getHeaders() }
    );
  }

  updatePrepaymentDetails(payload: any): Observable<any> {
    return this.http.put(
      URLPaths.URL_PREPAYMENT_DETAILS,
      { update_prepayment_details: payload },
      { headers: ApiheaderService.getHeaders() }
    );
  }
}

